<template>
  <main class="app-main contracts-page">
    <div class="app-content-header">
      <div class="container-fluid">
        <ol class="breadcrumb mb-3">
          <li class="breadcrumb-item"><router-link to="/">الرئيسية</router-link></li>
          <li class="breadcrumb-item active">منصة العقود العقارية</li>
        </ol>
        <div class="contract-heading">
          <div>
            <h1>
              <i class="bi bi-file-earmark-text ms-2" aria-hidden="true"></i>
              منصة العقود العقارية
            </h1>
            <p>إدارة العقود ومتابعة مدتها ومرفقاتها في مكان واحد</p>
          </div>
          <div class="contract-actions">
            <router-link v-if="canTemplates" to="/contracts/templates" class="btn btn-outline-primary">
              <i class="bi bi-folder2-open ms-1" aria-hidden="true"></i>
              مكتبة النماذج
            </router-link>
            <button
              v-if="canRead"
              class="btn btn-outline-primary"
              type="button"
              :disabled="exporting || loading"
              @click="exportRows"
            >
              <i class="bi bi-download ms-1" aria-hidden="true"></i>
              {{ exporting ? 'جارٍ التصدير...' : 'تصدير النتائج' }}
            </button>
            <router-link v-if="canCreate" to="/contracts/create" class="btn btn-primary">
              <i class="bi bi-plus-lg ms-1" aria-hidden="true"></i>
              إضافة عقد جديد
            </router-link>
          </div>
        </div>
      </div>
    </div>
    <div class="app-content">
      <div class="container-fluid">
        <div v-if="!canRead" class="contract-card contract-access-denied">ليس لديك صلاحية عرض العقود.</div>
        <template v-else>
          <errorAlert v-if="hasError" :alertMessage="message" :errorNumber="errorNumber" />
          <section class="contract-summary-grid" aria-label="إحصائيات جميع العقود">
            <div class="contract-stat contract-stat-featured">
              <div class="contract-stat-icon"><i class="bi bi-files" aria-hidden="true"></i></div>
              <div>
                <span>إجمالي العقود</span>
                <strong>{{ statsLoaded ? number(stats.total) : '—' }}</strong>
              </div>
            </div>
            <div class="contract-stat">
              <div class="contract-stat-icon"><i class="bi bi-check2-circle" aria-hidden="true"></i></div>
              <div>
                <span>العقود السارية</span>
                <strong>{{ statsLoaded ? number(stats.active) : '—' }}</strong>
              </div>
            </div>
            <div class="contract-stat">
              <div class="contract-stat-icon"><i class="bi bi-hourglass-split" aria-hidden="true"></i></div>
              <div>
                <span>قاربت على الانتهاء</span>
                <strong>{{ statsLoaded ? number(stats.expiringSoon) : '—' }}</strong>
              </div>
            </div>
            <div class="contract-stat">
              <div class="contract-stat-icon"><i class="bi bi-calendar-x" aria-hidden="true"></i></div>
              <div>
                <span>العقود المنتهية</span>
                <strong>{{ statsLoaded ? number(stats.expired) : '—' }}</strong>
              </div>
            </div>
            <div class="contract-stat">
              <div class="contract-stat-icon"><i class="bi bi-percent" aria-hidden="true"></i></div>
              <div>
                <span>متوسط النسبة</span>
                <strong>{{ statsLoaded ? `${percentage(stats.averagePercentage)}%` : '—' }}</strong>
              </div>
            </div>
          </section>
          <section class="contract-card mb-4" aria-labelledby="contracts-distribution-title">
            <div class="contract-card-header">
              <h2 id="contracts-distribution-title">توزيع العقود حسب النوع والحالة</h2>
              <span class="contract-secondary">
                إحصائيات جميع العقود · المعلقة: {{ statsLoaded ? number(stats.suspended) : '—' }}
              </span>
            </div>
            <div class="contract-card-body">
              <div v-if="!statsLoaded" class="contract-hint">جارٍ تحميل الإحصائيات...</div>
              <div v-else-if="typeStats.length" class="contract-type-grid">
                <button
                  v-for="type in typeStats"
                  :key="type.typeId"
                  type="button"
                  class="contract-type-stat contract-type-select"
                  :aria-pressed="String(appliedFilters.typeId) === String(type.typeId)"
                  :disabled="loading"
                  @click="selectType(String(type.typeId))"
                >
                  <div class="d-flex justify-content-between align-items-center gap-2">
                    <span class="contract-type-stat-label">{{ type.name }}</span>
                    <span class="type-count">{{ number(type.total) }}</span>
                  </div>
                  <div class="contract-distribution" aria-hidden="true">
                    <span class="distribution-active" :style="segmentStyle(type.active, type.total)"></span>
                    <span class="distribution-expiring" :style="segmentStyle(type.expiringSoon, type.total)"></span>
                    <span class="distribution-expired" :style="segmentStyle(type.expired, type.total)"></span>
                    <span class="distribution-suspended" :style="segmentStyle(type.suspended, type.total)"></span>
                  </div>
                  <div class="contract-type-states">
                    <span>ساري {{ number(type.active) }}</span>
                    <span>قارب الانتهاء {{ number(type.expiringSoon) }}</span>
                    <span>منتهي {{ number(type.expired) }}</span>
                    <span>معلق {{ number(type.suspended) }}</span>
                  </div>
                </button>
              </div>
              <p v-else class="contract-hint mb-0">لا توجد بيانات لعرض التوزيع.</p>
            </div>
          </section>
          <section class="card shadow card-warning card-outline mb-4 mt-5" aria-labelledby="contracts-filter-title">
            <div class="card-header">
              <button
                id="contracts-filter-title"
                class="filter-btn w-100 text-start collapse-toggle"
                type="button"
                data-bs-toggle="collapse"
                data-bs-target="#contractsFilterCard"
                aria-expanded="false"
                aria-controls="contractsFilterCard"
              >
                <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                بحث وتصفية
              </button>
            </div>
            <div id="contractsFilterCard" class="collapse">
              <form @submit.prevent="applyFilters">
                <div class="card-body">
                  <div class="row">
                    <div class="col-12 col-sm-6 mb-3">
                      <label for="contracts-search" class="visually-hidden">البحث في العقود</label>
                      <input
                        id="contracts-search"
                        v-model.trim="filters.search"
                        type="search"
                        class="form-control"
                        placeholder="اسم العقد، رقمه، العميل، الموظف أو الملاحظات"
                      />
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <label for="contracts-type" class="visually-hidden">نوع العقد</label>
                      <select id="contracts-type" v-model="filters.typeId" class="form-control">
                        <option value="">جميع أنواع العقود</option>
                        <option v-for="type in types" :key="type.id" :value="String(type.id)">{{ type.name }}</option>
                      </select>
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <label for="contracts-status" class="visually-hidden">حالة العقد</label>
                      <select id="contracts-status" v-model="filters.status" class="form-control">
                        <option value="">جميع الحالات الحالية</option>
                        <option v-for="status in statuses" :key="status.value" :value="status.value">
                          {{ status.label }}
                        </option>
                      </select>
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <label for="contracts-date-from" class="form-label">فترة العقد من</label>
                      <input
                        id="contracts-date-from"
                        v-model="filters.dateFrom"
                        type="date"
                        class="form-control"
                        :max="filters.dateTo || undefined"
                      />
                    </div>
                    <div class="col-6 col-sm-3 mb-3">
                      <label for="contracts-date-to" class="form-label">فترة العقد إلى</label>
                      <input
                        id="contracts-date-to"
                        v-model="filters.dateTo"
                        type="date"
                        class="form-control"
                        :min="filters.dateFrom || undefined"
                      />
                    </div>
                  </div>
                  <p class="contract-hint mb-0">
                    تُعرض العقود المتداخلة مع الفترة المحددة. لعرض العقود المنتهية اختر «منتهي» من فلتر الحالة.
                  </p>
                </div>
                <div class="card-footer">
                  <div class="row">
                    <div class="col text-end">
                      <button type="submit" class="btn btn-warning btn-sm" :disabled="loading">
                        <i class="fa-solid fa-filter" aria-hidden="true"></i>
                        بحث وتصفية
                      </button>
                      <button type="button" class="btn btn-light btn-sm ms-3" @click="clearFilters">
                        <i class="fa-solid fa-filter-circle-xmark" aria-hidden="true"></i>
                        إلغاء
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </section>
          <section class="contract-card mb-4" aria-labelledby="contracts-results-title">
            <div class="contract-card-header">
              <h2 id="contracts-results-title">سجل العقود</h2>
              <span class="contract-secondary">{{ number(countRows) }} نتيجة</span>
            </div>
            <div class="contract-tabs" aria-label="تصفية سريعة حسب نوع العقد">
              <button
                type="button"
                class="contract-tab"
                :aria-pressed="!appliedFilters.typeId"
                :disabled="loading"
                @click="selectType('')"
              >
                جميع الأنواع
              </button>
              <button
                v-for="type in types"
                :key="type.id"
                type="button"
                class="contract-tab"
                :aria-pressed="String(appliedFilters.typeId) === String(type.id)"
                :disabled="loading"
                @click="selectType(String(type.id))"
              >
                {{ type.name }}
              </button>
            </div>
            <div class="contract-tabs" aria-label="تصفية سريعة حسب حالة العقد">
              <button
                v-for="status in visibleStatusTabs"
                :key="status.value"
                type="button"
                class="contract-tab"
                :aria-pressed="appliedFilters.status === status.value"
                :disabled="loading"
                @click="selectStatus(status.value)"
              >
                {{ status.label }}
              </button>
            </div>
            <div v-if="loading" class="py-4"><mainLoader /></div>
            <div v-else-if="hasError" class="contract-empty">
              <i class="bi bi-exclamation-circle" aria-hidden="true"></i>
              <h3>تعذر تحميل العقود</h3>
              <button type="button" class="btn btn-outline-primary mt-2" @click="loadList">إعادة المحاولة</button>
            </div>
            <div v-else-if="!rows.length" class="contract-empty">
              <i class="bi bi-file-earmark-text" aria-hidden="true"></i>
              <h3>لا توجد عقود مطابقة</h3>
              <p>يمكنك تعديل معايير البحث أو إضافة عقد جديد.</p>
              <router-link v-if="canCreate" to="/contracts/create" class="btn btn-primary">إضافة عقد جديد</router-link>
            </div>
            <div v-else class="table-responsive">
              <table class="table contract-table">
                <thead>
                  <tr>
                    <th>العقد / النوع</th>
                    <th>العميل / الطرف الثاني</th>
                    <th>جالب العقد</th>
                    <th>العقارات</th>
                    <th>مدة العقد</th>
                    <th>النسبة</th>
                    <th>الحالة</th>
                    <th>الملاحظات</th>
                    <th>المرفق</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in rows" :key="row.id">
                    <td style="min-width: 210px">
                      <router-link :to="`/contracts/view/${row.id}`" class="contract-title-link">
                        {{ row.title }}
                      </router-link>
                      <div class="contract-secondary mt-1">{{ row.code }}</div>
                      <div class="contract-secondary">{{ row.typeName }}</div>
                      <router-link
                        v-if="row.parentContractId"
                        :to="`/contracts/view/${row.parentContractId}`"
                        class="contract-secondary"
                      >
                        ملحق للعقد {{ row.parentContractCode }}
                      </router-link>
                      <div v-else-if="row.hasAnnex" class="contract-secondary">يتضمن ملحقاً</div>
                    </td>
                    <td>
                      {{ row.clientName || '—' }}
                      <div class="contract-secondary contract-phone">{{ row.clientMobile }}</div>
                    </td>
                    <td>
                      {{ row.employeeName || '—' }}
                      <div class="contract-secondary contract-phone">{{ row.employeeMobile }}</div>
                    </td>
                    <td>{{ number(row.propertiesCount) }}</td>
                    <td class="text-nowrap">
                      <div>{{ dateOnly(row.startDate) }}</div>
                      <div class="contract-secondary">إلى {{ dateOnly(row.endDate) }}</div>
                    </td>
                    <td>{{ percentage(row.percentage) }}%</td>
                    <td>
                      <span class="contract-status" :class="`contract-status-${row.effectiveStatus}`">
                        {{ statusLabel(row.effectiveStatus) }}
                      </span>
                      <select
                        v-if="canUpdate"
                        :value="row.status"
                        class="form-select form-select-sm mt-2"
                        style="min-width: 145px"
                        :disabled="busyStatusId === row.id"
                        :aria-label="`تعديل حالة ${row.code}`"
                        @change="changeStatus(row, $event)"
                      >
                        <option v-for="status in statuses" :key="status.value" :value="status.value">
                          {{ status.label }}
                        </option>
                      </select>
                      <div v-if="row.status !== row.effectiveStatus" class="contract-secondary mt-1">
                        الحالة محسوبة حسب مدة العقد
                      </div>
                    </td>
                    <td>
                      <form
                        v-if="editingNotesId === row.id"
                        class="contract-inline-form"
                        @submit.prevent="saveNotes(row)"
                      >
                        <label :for="`contract-notes-${row.id}`" class="visually-hidden">ملاحظات العقد</label>
                        <textarea
                          :id="`contract-notes-${row.id}`"
                          v-model="notesDraft"
                          class="form-control form-control-sm"
                          rows="3"
                          maxlength="10000"
                          :disabled="savingNotes"
                        ></textarea>
                        <div class="d-flex gap-2">
                          <button type="submit" class="btn btn-primary btn-sm" :disabled="savingNotes">
                            {{ savingNotes ? 'جارٍ الحفظ...' : 'حفظ' }}
                          </button>
                          <button
                            type="button"
                            class="btn btn-outline-secondary btn-sm"
                            :disabled="savingNotes"
                            @click="editingNotesId = null"
                          >
                            إلغاء
                          </button>
                        </div>
                      </form>
                      <button
                        v-else-if="canUpdate"
                        type="button"
                        class="contract-notes-button"
                        :title="row.notes || 'إضافة ملاحظات'"
                        @click="editNotes(row)"
                      >
                        {{ row.notes || 'إضافة ملاحظات' }}
                        <i class="bi bi-pencil-square" aria-hidden="true"></i>
                      </button>
                      <div v-else class="contract-preserve" style="max-width: 225px">{{ row.notes || '—' }}</div>
                    </td>
                    <td>
                      <button
                        v-if="row.attachment"
                        type="button"
                        class="btn btn-link btn-sm p-0 text-decoration-none contract-file-name"
                        :disabled="downloadingId === row.id"
                        @click="downloadAttachment(row)"
                      >
                        <i class="bi bi-paperclip" aria-hidden="true"></i>
                        {{ downloadingId === row.id ? 'جارٍ التنزيل...' : row.attachment.name }}
                      </button>
                      <span v-else class="contract-secondary">لا يوجد</span>
                    </td>
                    <td>
                      <div class="d-flex gap-1">
                        <router-link
                          :to="`/contracts/view/${row.id}`"
                          class="contract-icon-button"
                          title="تفاصيل العقد"
                          :aria-label="`تفاصيل ${row.code}`"
                        >
                          <i class="bi bi-eye" aria-hidden="true"></i>
                        </router-link>
                        <router-link
                          v-if="canUpdate"
                          :to="`/contracts/update/${row.id}`"
                          class="contract-icon-button"
                          title="تعديل العقد"
                          :aria-label="`تعديل ${row.code}`"
                        >
                          <i class="bi bi-pencil-square" aria-hidden="true"></i>
                        </router-link>
                        <router-link
                          v-if="canCreate && row.typeKey === 'property_management'"
                          :to="{
                            path: '/contracts/create',
                            query: { type: 'management_annex', parentContractId: row.id },
                          }"
                          class="contract-icon-button"
                          title="إضافة ملحق مرتبط"
                          :aria-label="`إضافة ملحق للعقد ${row.code}`"
                        >
                          <i class="bi bi-file-earmark-plus" aria-hidden="true"></i>
                        </router-link>
                        <button
                          v-if="canDelete"
                          type="button"
                          class="contract-icon-button danger"
                          :disabled="deletingId !== null"
                          title="حذف العقد"
                          :aria-label="`حذف ${row.code}`"
                          @click="deleteContract(row)"
                        >
                          <i class="bi bi-trash" aria-hidden="true"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div class="contract-card-footer">
              <span class="contract-secondary">صفحة {{ number(page) }} من {{ number(totalPages) }}</span>
              <nav aria-label="صفحات العقود">
                <ul class="pagination pagination-sm">
                  <li class="page-item" :class="{ disabled: page <= 1 || loading }">
                    <button
                      type="button"
                      class="page-link"
                      :disabled="page <= 1 || loading"
                      @click="goToPage(page - 1)"
                    >
                      السابق
                    </button>
                  </li>
                  <li v-for="item in pageNumbers" :key="item" class="page-item" :class="{ active: item === page }">
                    <button
                      type="button"
                      class="page-link"
                      :aria-current="item === page ? 'page' : undefined"
                      :disabled="loading"
                      @click="goToPage(item)"
                    >
                      {{ item }}
                    </button>
                  </li>
                  <li class="page-item" :class="{ disabled: page >= totalPages || loading }">
                    <button
                      type="button"
                      class="page-link"
                      :disabled="page >= totalPages || loading"
                      @click="goToPage(page + 1)"
                    >
                      التالي
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          </section>
        </template>
      </div>
    </div>
  </main>
</template>

<script>
import { checkAccess, toast } from '@/helpers/tools';
import {
  contractStatuses,
  statusLabel,
  number,
  percentage,
  dateOnly,
  authenticatedDownload,
  errorMessage,
  today,
} from './helpers';
import './contracts.css';

const emptyFilters = () => ({ search: '', typeId: '', status: '', dateFrom: '', dateTo: '' });
const queryText = (value) => String(Array.isArray(value) ? value[0] || '' : value || '');
export default {
  name: 'ContractsList',
  data() {
    const filters = emptyFilters();
    Object.keys(filters).forEach((key) => {
      filters[key] = queryText(this.$route.query[key]);
    });
    return {
      filters,
      appliedFilters: { ...filters },
      page: Math.max(1, parseInt(this.$route.query.page, 10) || 1),
      rows: [],
      types: [],
      statuses: contractStatuses,
      stats: {},
      statsLoaded: false,
      typeStats: [],
      countRows: 0,
      loading: true,
      hasError: false,
      message: '',
      errorNumber: 0,
      serial: 0,
      exporting: false,
      downloadingId: null,
      deletingId: null,
      busyStatusId: null,
      editingNotesId: null,
      notesDraft: '',
      savingNotes: false,
    };
  },
  computed: {
    canRead() {
      return checkAccess('contracts_read');
    },
    canCreate() {
      return checkAccess('contracts_create');
    },
    canUpdate() {
      return checkAccess('contracts_update');
    },
    canDelete() {
      return checkAccess('contracts_delete');
    },
    canTemplates() {
      return this.canRead || this.canCreate || this.canUpdate || checkAccess('contract_templates_manage');
    },
    totalPages() {
      return Math.max(1, Math.ceil(this.countRows / 20));
    },
    pageNumbers() {
      const start = Math.max(1, Math.min(this.page - 2, this.totalPages - 4));
      return Array.from({ length: Math.min(5, this.totalPages) }, (_, index) => start + index);
    },
    visibleStatusTabs() {
      return [
        { value: '', label: 'جميع العقود الحالية' },
        ...this.statuses.filter((item) => item.value !== 'expired' || this.appliedFilters.status === 'expired'),
      ];
    },
  },
  watch: {
    '$route.query'(query) {
      if (this.$route.path !== '/contracts') return;
      const filters = emptyFilters();
      Object.keys(filters).forEach((key) => {
        filters[key] = queryText(query[key]);
      });
      const page = Math.max(1, parseInt(query.page, 10) || 1);
      if (page === this.page && Object.keys(filters).every((key) => filters[key] === this.appliedFilters[key])) return;
      this.filters = filters;
      this.appliedFilters = { ...filters };
      this.page = page;
      this.editingNotesId = null;
      this.loadList();
    },
  },
  methods: {
    number,
    percentage,
    dateOnly,
    statusLabel,
    segmentStyle(value, total) {
      return { width: `${Number(total) > 0 ? (Number(value || 0) / Number(total)) * 100 : 0}%` };
    },
    params() {
      const params = {};
      for (const [key, value] of Object.entries(this.appliedFilters)) if (value !== '') params[key] = value;
      return params;
    },
    async loadList() {
      if (!this.canRead) {
        this.loading = false;
        return;
      }
      const serial = ++this.serial;
      this.loading = true;
      this.hasError = false;
      const params = { ...this.params(), page: this.page };
      this.$router.replace({ query: params }).catch(() => {});
      try {
        const { data } = await this.$http.get('/api/admin/contracts/list', { params });
        if (serial !== this.serial) return;
        this.rows = Array.isArray(data.rows) ? data.rows : [];
        this.countRows = Number(data.countRows) || 0;
        this.stats = data.stats || {};
        this.statsLoaded = true;
        this.typeStats = Array.isArray(data.typeStats) ? data.typeStats : [];
        if (this.page > this.totalPages) {
          this.page = this.totalPages;
          this.loadList();
        }
      } catch (error) {
        if (serial !== this.serial) return;
        [this.message, this.errorNumber] = this.$globalError(error, true);
        this.hasError = true;
      } finally {
        if (serial === this.serial) this.loading = false;
      }
    },
    async loadOptions() {
      if (!this.canRead) return;
      try {
        const { data } = await this.$http.get('/api/admin/contracts/options');
        this.types = Array.isArray(data.types) ? data.types : [];
        if (data.statuses?.length) this.statuses = data.statuses;
      } catch (error) {
        toast(this.$globalError(error));
      }
    },
    applyFilters() {
      if (this.filters.dateFrom && this.filters.dateTo && this.filters.dateFrom > this.filters.dateTo)
        return toast('تاريخ بداية الفترة يجب أن يسبق نهايتها');
      this.appliedFilters = { ...this.filters };
      this.page = 1;
      this.editingNotesId = null;
      this.loadList();
    },
    clearFilters() {
      this.filters = emptyFilters();
      this.applyFilters();
    },
    selectType(typeId) {
      this.filters = { ...this.appliedFilters, typeId };
      this.applyFilters();
    },
    selectStatus(status) {
      this.filters = { ...this.appliedFilters, status };
      this.applyFilters();
    },
    goToPage(page) {
      if (page < 1 || page > this.totalPages || page === this.page) return;
      this.page = page;
      this.editingNotesId = null;
      this.loadList();
    },
    editNotes(row) {
      if (this.savingNotes) return;
      this.editingNotesId = row.id;
      this.notesDraft = row.notes || '';
    },
    async saveNotes(row) {
      if (!this.canUpdate || this.savingNotes) return;
      const serial = this.serial;
      this.savingNotes = true;
      try {
        await this.$http.patch(`/api/admin/contracts/notes/${row.id}`, { notes: this.notesDraft });
        if (serial !== this.serial) return;
        this.editingNotesId = null;
        toast('تم حفظ الملاحظات', 'success');
        await this.loadList();
      } catch (error) {
        if (serial === this.serial) toast(this.$globalError(error));
      } finally {
        this.savingNotes = false;
      }
    },
    async changeStatus(row, event) {
      const status = event.target.value;
      if (!this.canUpdate || this.busyStatusId !== null || status === row.status) {
        event.target.value = row.status;
        return;
      }
      const serial = this.serial;
      this.busyStatusId = row.id;
      try {
        await this.$http.patch(`/api/admin/contracts/status/${row.id}`, { status });
        if (serial !== this.serial) return;
        toast('تم تحديث حالة العقد', 'success');
        await this.loadList();
      } catch (error) {
        if (serial === this.serial) {
          event.target.value = row.status;
          toast(this.$globalError(error));
        }
      } finally {
        this.busyStatusId = null;
      }
    },
    async deleteContract(row) {
      if (!this.canDelete || this.deletingId !== null) return;
      const serial = this.serial;
      const confirmation = await this.$swal.fire({
        title: 'حذف العقد؟',
        text: `${row.code} — ${row.title}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'حذف',
        cancelButtonText: 'إلغاء',
        confirmButtonColor: '#dc3545',
      });
      if (!confirmation.isConfirmed || serial !== this.serial) return;
      this.deletingId = row.id;
      try {
        await this.$http.delete(`/api/admin/contracts/delete/${row.id}`);
        if (serial !== this.serial) return;
        toast('تم حذف العقد', 'success');
        await this.loadList();
      } catch (error) {
        if (serial === this.serial) toast(this.$globalError(error));
      } finally {
        this.deletingId = null;
      }
    },
    async downloadAttachment(row) {
      if (this.downloadingId !== null) return;
      this.downloadingId = row.id;
      try {
        await authenticatedDownload(this.$http, `/api/admin/contracts/attachment/${row.id}`, row.attachment.name);
      } catch (error) {
        toast(await errorMessage(this, error));
      } finally {
        this.downloadingId = null;
      }
    },
    async exportRows() {
      if (this.exporting || !this.canRead) return;
      this.exporting = true;
      try {
        const query = new URLSearchParams(this.params()).toString();
        await authenticatedDownload(
          this.$http,
          `/api/admin/contracts/export${query ? `?${query}` : ''}`,
          `contracts-${today()}.csv`
        );
      } catch (error) {
        toast(await errorMessage(this, error));
      } finally {
        this.exporting = false;
      }
    },
  },
  created() {
    this.loadOptions();
    this.loadList();
  },
  beforeUnmount() {
    this.serial += 1;
  },
};
</script>
